import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, Sparkles, Send, ShieldCheck, Clock, X } from "lucide-react";

const CADENCE_OPTIONS = [
  { v: "daily", label: "Daily" },
  { v: "weekly", label: "Weekly" },
  { v: "monthly", label: "Monthly" },
  { v: "none", label: "No reminders" },
];

function AddGoalDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [cadence, setCadence] = useState("weekly");
  const [target_date, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/goals", { title, detail, cadence, target_date: target_date || null });
      toast.success("Goal saved");
      onCreated(data);
      setTitle(""); setDetail(""); setCadence("weekly"); setTarget("");
      onClose();
    } catch { toast.error("Could not save goal"); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="add-goal-dialog" className="fixed inset-0 z-50 grid place-items-center bg-black/70 backdrop-blur-sm p-4">
      <form onSubmit={submit} className="w-full max-w-lg bg-[#0D0E15] border border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-[Unbounded] text-lg tracking-tighter">New goal</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white"><X size={16} /></button>
        </div>
        <div>
          <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Title</label>
          <input data-testid="goal-title-input" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus
            placeholder="Learn Portuguese basics"
            className="mt-2 w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none" />
        </div>
        <div>
          <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Detail</label>
          <textarea data-testid="goal-detail-input" value={detail} onChange={(e) => setDetail(e.target.value)} rows={2}
            placeholder="Why this matters + first step"
            className="mt-2 w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Cadence</label>
            <select data-testid="goal-cadence-select" value={cadence} onChange={(e) => setCadence(e.target.value)}
              className="mt-2 w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none">
              {CADENCE_OPTIONS.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Target date</label>
            <input data-testid="goal-target-input" type="date" value={target_date} onChange={(e) => setTarget(e.target.value)}
              className="mt-2 w-full bg-[#05050A] border border-white/10 rounded-lg px-4 py-3 focus:border-[#00E5FF] focus:outline-none" />
          </div>
        </div>
        <button data-testid="submit-goal-btn" disabled={busy} className="w-full bg-[#00E5FF] text-black font-semibold py-3 rounded-full disabled:opacity-50">
          {busy ? "Saving…" : "Create goal"}
        </button>
      </form>
    </div>
  );
}

function GoalCard({ g, onComplete, onDelete, onSnooze }) {
  const now = new Date().toISOString();
  const due = g.status === "open" && g.next_reminder_at && g.next_reminder_at <= now;
  return (
    <div data-testid={`goal-card-${g.id}`} className={`p-4 rounded-2xl border ${due ? "border-[#FF007F]/60 bg-[#FF007F]/5" : "border-white/10 bg-[#0D0E15]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className={`font-[Unbounded] text-base tracking-tighter ${g.status === "completed" ? "line-through text-white/40" : ""}`}>{g.title}</div>
          {g.detail && <div className="text-xs text-white/60 mt-1">{g.detail}</div>}
          <div className="flex gap-3 mt-2 text-[10px] font-mono uppercase tracking-widest text-white/40">
            <span>Cadence: {g.cadence}</span>
            {g.target_date && <span>Target: {g.target_date}</span>}
            {due && <span className="text-[#FF007F]">Reminder due</span>}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          {g.status === "open" && due && (
            <button data-testid={`goal-snooze-${g.id}`} onClick={() => onSnooze(g)}
              title="Snooze"
              className="p-2 rounded-full text-white/60 hover:text-[#00E5FF] hover:bg-white/5 transition-all">
              <Clock size={14} />
            </button>
          )}
          {g.status === "open" && (
            <button data-testid={`goal-complete-${g.id}`} onClick={() => onComplete(g)}
              title="Mark complete"
              className="p-2 rounded-full text-white/60 hover:text-[#39FF14] hover:bg-white/5 transition-all">
              <CheckCircle2 size={14} />
            </button>
          )}
          <button data-testid={`goal-delete-${g.id}`} onClick={() => onDelete(g)}
            title="Delete"
            className="p-2 rounded-full text-white/60 hover:text-[#FF007F] hover:bg-white/5 transition-all">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function SuggestionCard({ s, sigSet, onApproved }) {
  const [sig, setSig] = useState("");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null); // 'approved' | 'rejected'

  const approve = async () => {
    if (!sig) { toast.error("Enter your signature to approve"); return; }
    setBusy(true);
    try {
      await api.post("/ai/approve", { suggestion_id: s.id, suggestion_title: s.title, signature: sig });
      setStatus("approved");
      toast.success("Approved");
      onApproved?.(s);
    } catch (err) {
      setStatus("rejected");
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally { setBusy(false); setSig(""); }
  };

  return (
    <div data-testid={`suggestion-${s.id}`} className="mt-2 p-3 rounded-xl border border-[#00E5FF]/30 bg-[#00E5FF]/5">
      <div className="flex items-start gap-2">
        <Sparkles size={14} className="text-[#00E5FF] mt-1 shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-semibold">{s.title}</div>
          <div className="text-xs text-white/60 mt-1">{s.description}</div>
        </div>
      </div>
      {status === "approved" ? (
        <div data-testid={`approved-${s.id}`} className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#39FF14] inline-flex items-center gap-1">
          <ShieldCheck size={12} /> Approved by signature
        </div>
      ) : !sigSet ? (
        <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-white/40">Set a signature in Profile to approve</div>
      ) : (
        <div className="mt-3 flex gap-2">
          <input
            data-testid={`sig-input-${s.id}`}
            type="password"
            value={sig}
            onChange={(e) => setSig(e.target.value)}
            placeholder="Approve with signature"
            className="flex-1 bg-[#05050A] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#00E5FF] focus:outline-none"
          />
          <button
            data-testid={`approve-btn-${s.id}`}
            onClick={approve}
            disabled={busy}
            className="px-3 py-2 rounded-lg bg-[#00E5FF] text-black text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50"
          >
            <ShieldCheck size={12} /> Approve
          </button>
        </div>
      )}
    </div>
  );
}

function AdvisorPanel({ sigSet }) {
  const [messages, setMessages] = useState([
    { role: "assistant", reply: "Hi — I'm your AI Advisor. Tell me a goal you want to move forward, and I'll propose the next best step. I never execute; every action needs your signature.", suggestions: [] },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottom = useRef(null);
  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { role: "user", reply: text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/advisor", { message: text });
      setMessages((m) => [...m, { role: "assistant", reply: data.reply, suggestions: data.suggestions || [] }]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Advisor unavailable");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-[#0D0E15] overflow-hidden flex flex-col h-[70vh] max-h-[720px]">
      <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
        <Sparkles size={14} className="text-[#00E5FF]" />
        <div className="font-[Unbounded] text-sm tracking-tight">AI ADVISOR</div>
        <div className="ml-auto text-[10px] font-mono uppercase tracking-widest text-white/40">propose-only · signature-gated</div>
      </div>
      <div data-testid="advisor-messages" className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : ""}>
            <div className={`inline-block max-w-[85%] text-left px-3 py-2 rounded-2xl text-sm ${
              m.role === "user"
                ? "bg-[#00E5FF] text-black rounded-br-sm"
                : "bg-white/5 border border-white/10 rounded-bl-sm"
            }`}>
              {m.reply}
            </div>
            {m.role === "assistant" && (m.suggestions || []).map((s) => (
              <SuggestionCard key={s.id + i} s={s} sigSet={sigSet} />
            ))}
          </div>
        ))}
        {busy && (
          <div className="text-xs text-white/40 font-mono">advisor thinking…</div>
        )}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2">
        <input
          data-testid="advisor-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask your advisor…"
          className="flex-1 bg-[#05050A] border border-white/10 rounded-full px-4 py-2 text-sm focus:border-[#00E5FF] focus:outline-none"
        />
        <button data-testid="advisor-send-btn" disabled={busy || !input.trim()}
          className="px-4 py-2 rounded-full bg-[#00E5FF] text-black text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
          <Send size={14} /> Send
        </button>
      </form>
    </div>
  );
}

export default function Goals() {
  const [goals, setGoals] = useState([]);
  const [dueCount, setDueCount] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [sigSet, setSigSet] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data.goals || []);
      setDueCount(data.due_count || 0);
    } catch { /* ignore */ }
  };
  const loadSig = async () => {
    try {
      const { data } = await api.get("/auth/signature/status");
      setSigSet(!!data.set);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); loadSig(); }, []);

  const onComplete = async (g) => {
    await api.post(`/goals/${g.id}/complete`);
    toast.success("Marked complete");
    load();
  };
  const onDelete = async (g) => {
    await api.delete(`/goals/${g.id}`);
    toast.success("Goal removed");
    load();
  };
  const onSnooze = async (g) => {
    await api.post(`/goals/${g.id}/snooze`);
    toast.success("Snoozed");
    load();
  };

  const open = goals.filter((g) => g.status === "open");
  const done = goals.filter((g) => g.status !== "open");

  return (
    <div className="px-4 md:px-8 py-6 max-w-6xl mx-auto">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs tracking-[0.3em] font-mono text-[#00E5FF]">// GOALS + ADVISOR</div>
          <h1 className="font-[Unbounded] text-3xl md:text-4xl font-bold tracking-tighter mt-2">What are you moving on?</h1>
          <p className="text-white/50 text-sm mt-2">Set goals. Get concise coaching. Approve every AI-suggested action with your private signature.</p>
        </div>
        <button data-testid="add-goal-btn" onClick={() => setOpenDialog(true)}
          className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-semibold px-5 py-2.5 rounded-full hover:glow-primary transition-all">
          <Plus size={16} /> Add goal
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] tracking-widest font-mono text-white/50 uppercase">Open · {open.length}</div>
            {dueCount > 0 && (
              <div data-testid="due-count" className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F]">{dueCount} due</div>
            )}
          </div>
          <div className="space-y-3">
            {open.length === 0 && (
              <div data-testid="empty-goals" className="p-6 rounded-2xl border border-dashed border-white/10 text-center text-sm text-white/50">
                No open goals yet. Add one to get started.
              </div>
            )}
            {open.map((g) => (
              <GoalCard key={g.id} g={g} onComplete={onComplete} onDelete={onDelete} onSnooze={onSnooze} />
            ))}
          </div>

          {done.length > 0 && (
            <>
              <div className="text-[10px] tracking-widest font-mono text-white/40 uppercase mt-8 mb-3">Completed · {done.length}</div>
              <div className="space-y-3">
                {done.map((g) => (
                  <GoalCard key={g.id} g={g} onComplete={onComplete} onDelete={onDelete} onSnooze={onSnooze} />
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          {!sigSet && (
            <div data-testid="signature-warning" className="mb-3 p-3 rounded-xl border border-[#FF007F]/40 bg-[#FF007F]/5 text-xs text-white/70">
              Set a signature in <span className="font-mono">Profile</span> to approve AI suggestions.
            </div>
          )}
          <AdvisorPanel sigSet={sigSet} />
        </div>
      </div>

      <AddGoalDialog open={openDialog} onClose={() => setOpenDialog(false)} onCreated={() => load()} />
    </div>
  );
}
